import React from "react";
import { Typography } from "@mui/material";
import CustomDialog from "../CustomDialog";
import CustomButton from "../CustomButton";
import { COLORS } from "../../assets/theme";
import { formatMoney } from "./classUtils";

interface NoActivePackageConfirmDialogProps {
  open: boolean;
  clientName?: string;
  dropInPrice?: number | string;
  submitting?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Shown when a client has no currently-usable package — a purchased package
 * only becomes usable once it's paid, fully or partially, so "has a package"
 * and "can redeem a package" aren't the same thing. Booking must not fall
 * through to an invoice silently; staff confirm it explicitly here.
 *
 * Single-session booking only — block enrollment prices itself through
 * /enroll/quote, which already itemises what a package covers and what is
 * invoiced.
 */
const NoActivePackageConfirmDialog: React.FC<NoActivePackageConfirmDialogProps> = ({
  open,
  clientName,
  dropInPrice,
  submitting = false,
  onClose,
  onConfirm,
}) => {
  const priceLabel = formatMoney(dropInPrice);
  return (
    <CustomDialog
      open={open}
      onClose={() => {
        if (!submitting) onClose();
      }}
      title="No active package"
      actions={
        <>
          <CustomButton
            label="Cancel"
            variant="outlined"
            onClick={onClose}
            disabled={submitting}
          />
          <CustomButton
            label={`Proceed — Invoice ${priceLabel}`}
            variant="contained"
            bgColor={COLORS.MainColor}
            textColor={COLORS.WhiteText}
            loading={submitting}
            disabled={submitting}
            onClick={onConfirm}
          />
        </>
      }
    >
      <Typography sx={{ fontSize: 14 }}>
        {`${
          clientName || "This client"
        } has no active package for this class — a package only counts as active once it's paid, fully or partially. Proceed by creating an invoice at the drop-in price instead?`}
      </Typography>
    </CustomDialog>
  );
};

export default NoActivePackageConfirmDialog;
